import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header, Footer } from "../components/layout/SiteChrome";
import { sendOTP, verifyOTP } from "../services/api";
import { authStore, useAuth } from "../store/authStore";

const inputStyle: React.CSSProperties = {
  width: "100%", background: "var(--bg)", border: "1.5px solid var(--border)",
  borderRadius: 12, padding: "13px 16px", fontSize: 15, color: "var(--text)",
  fontFamily: "var(--font-body)", outline: "none", transition: "border-color 0.2s",
};

const inputFocus = (e: React.FocusEvent<HTMLInputElement>) => { e.target.style.borderColor = "var(--brand)"; };
const inputBlur  = (e: React.FocusEvent<HTMLInputElement>) => { e.target.style.borderColor = "var(--border)"; };

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/";
  const { isLoggedIn, loaded } = useAuth();
  const nav = useNavigate();

  const [mode, setMode] = useState<"login" | "signup">("signup");
  const [step, setStep] = useState<"details" | "otp">("details");

  const [name, setName]     = useState("");
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");
  const [otp, setOtp]       = useState("");
  const [timer, setTimer]   = useState(0);
  const [error, setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const iRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (loaded && isLoggedIn) nav(redirect, { replace: true });
  }, [loaded, isLoggedIn, nav, redirect]);

  useEffect(() => {
    if (timer <= 0) return;
    iRef.current = setInterval(() => setTimer(t => t - 1), 1000);
    return () => { if (iRef.current) clearInterval(iRef.current); };
  }, [timer]);

  const validatePhone = () => {
    if (!/^[6-9]\d{9}$/.test(phone)) { setError("Enter a valid 10-digit Indian mobile number"); return false; }
    return true;
  };

  const validateSignupFields = () => {
    if (!name.trim()) { setError("Please enter your name"); return false; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Please enter a valid email address"); return false; }
    if (!validatePhone()) return false;
    return true;
  };

  const handleSendOTP = async () => {
    if (mode === "signup" && !validateSignupFields()) return;
    if (mode === "login" && !validatePhone()) return;

    setError(""); setLoading(true);
    try {
      await sendOTP(phone);
      setStep("otp"); setTimer(60);
    } catch (e: any) { setError(e.message || "Failed to send OTP. Try again."); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    setError(""); setLoading(true);
    try {
      const payload: any = { phone, otp };
      if (mode === "signup") {
        if (name.trim()) payload.name = name.trim();
        if (email.trim()) payload.email = email.trim();
      }
      const data = await verifyOTP(phone, otp);

      // If signup, send name/email to backend
      if (mode === "signup" && (name.trim() || email.trim())) {
        try {
          const token = data.token;
          await fetch(`${process.env.REACT_APP_API_URL}/api/customer/auth/profile`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: name.trim(), email: email.trim() }),
          });
        } catch { /* profile update is best-effort */ }
      }

      authStore.login(data.token, {
        _id: data.customer._id,
        phone,
        name: mode === "signup" ? name.trim() || undefined : data.customer.name,
        email: mode === "signup" ? email.trim() || undefined : data.customer.email,
      });
      nav(redirect, { replace: true });
    } catch (e: any) { setError(e.message || "Incorrect OTP. Please try again."); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setOtp(""); setTimer(60); setError("");
    try { await sendOTP(phone); } catch (e: any) { setError(e.message || "Failed to resend."); }
  };

  const switchMode = (newMode: "login" | "signup") => {
    setMode(newMode);
    setStep("details");
    setOtp(""); setError(""); setTimer(0);
  };

  return (
    <div className="page-bg">
      <Header />

      <section style={{ minHeight: "calc(100vh - 180px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
        <div style={{ width: "100%", maxWidth: 420 }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text2)", textDecoration: "none", marginBottom: 28, transition: "color 0.2s" }}
            onMouseEnter={e => (e.currentTarget.style.color = "var(--brand)")}
            onMouseLeave={e => (e.currentTarget.style.color = "var(--text2)")}>
            ← Back to Home
          </Link>

          <div style={{
            background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 20,
            padding: "36px 32px", boxShadow: "var(--shadow-lg)",
          }}>
            {/* Icon */}
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16, background: "var(--brand-light)",
                border: "2px solid rgba(255,107,53,0.2)", display: "flex", alignItems: "center",
                justifyContent: "center", margin: "0 auto 14px",
              }}>
                {mode === "signup" ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                )}
              </div>
              <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: "var(--text)", marginBottom: 4 }}>
                {step === "details"
                  ? (mode === "signup" ? "Create Account" : "Welcome Back")
                  : "Verify OTP"}
              </h1>
              <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>
                {step === "details"
                  ? (mode === "signup" ? "Fill in your details to get started" : "Enter your phone number to login")
                  : <>A 6-digit code was sent to <strong>+91 {phone}</strong> via WhatsApp</>}
              </p>
            </div>

            {/* Login/Signup tabs — only on details step */}
            {step === "details" && (
              <div style={{ display: "flex", background: "var(--bg)", borderRadius: 10, padding: 4, marginBottom: 24, border: "1px solid var(--border)" }}>
                {(["signup", "login"] as const).map(m => (
                  <button key={m} onClick={() => switchMode(m)} style={{
                    flex: 1, padding: "10px", borderRadius: 8, border: "none", fontSize: 14, fontWeight: 700,
                    fontFamily: "var(--font-display)", cursor: "pointer", transition: "all 0.2s",
                    background: mode === m ? "var(--surface)" : "transparent",
                    color: mode === m ? "var(--brand)" : "var(--text2)",
                    boxShadow: mode === m ? "var(--shadow-sm)" : "none",
                  }}>
                    {m === "signup" ? "Sign Up" : "Login"}
                  </button>
                ))}
              </div>
            )}

            {/* ── DETAILS STEP ── */}
            {step === "details" && (
              <>
                {mode === "signup" && (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Full Name</label>
                      <input type="text" value={name} onChange={e => { setName(e.target.value); setError(""); }}
                        placeholder="e.g. Rahul Sharma" style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Email Address</label>
                      <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
                        placeholder="e.g. rahul@gmail.com" style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
                    </div>
                  </>
                )}

                <div style={{ marginBottom: mode === "signup" ? 0 : 0 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>Phone Number</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ background: "var(--surface2)", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontWeight: 600, color: "var(--text2)", whiteSpace: "nowrap", flexShrink: 0, border: "1.5px solid var(--border)" }}>+91</span>
                    <input type="tel" maxLength={10} value={phone}
                      onChange={e => { setPhone(e.target.value.replace(/\D/g, "")); setError(""); }}
                      onKeyDown={e => { if (e.key === "Enter") handleSendOTP(); }}
                      placeholder="10-digit mobile number"
                      style={{ ...inputStyle, flex: 1, minWidth: 0 }} onFocus={inputFocus} onBlur={inputBlur} />
                  </div>
                </div>

                {error && <p style={{ marginTop: 10, fontSize: 13, color: "var(--danger)" }}>⚠ {error}</p>}

                <button onClick={handleSendOTP} disabled={loading}
                  style={{
                    marginTop: 18, width: "100%", borderRadius: 12,
                    background: loading ? "var(--surface2)" : "linear-gradient(135deg, var(--brand), var(--brand-dark))",
                    color: loading ? "var(--text2)" : "#fff", padding: "14px", fontSize: 15, fontWeight: 700,
                    fontFamily: "var(--font-display)", cursor: loading ? "not-allowed" : "pointer",
                    border: "none", transition: "all 0.2s",
                    boxShadow: loading ? "none" : "0 4px 14px rgba(255,107,53,0.35)",
                  }}>
                  {loading ? "Sending..." : "Send OTP"}
                </button>
              </>
            )}

            {/* ── OTP STEP ── */}
            {step === "otp" && (
              <>
                <input type="text" inputMode="numeric" maxLength={6} value={otp} autoFocus
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, "")); setError(""); }}
                  onKeyDown={e => { if (e.key === "Enter" && otp.length === 6) handleVerify(); }}
                  placeholder="• • • • • •"
                  style={{
                    width: "100%", textAlign: "center", letterSpacing: "0.5em", fontSize: 28,
                    fontWeight: 700, color: "var(--text)", background: "var(--bg)",
                    border: "1.5px solid var(--border)", borderRadius: 12, padding: "16px",
                    outline: "none", fontFamily: "var(--font-display)", transition: "border-color 0.2s",
                  }}
                  onFocus={e => e.target.style.borderColor = "var(--brand)"}
                  onBlur={e => e.target.style.borderColor = "var(--border)"} />

                {error && <p style={{ marginTop: 10, fontSize: 13, color: "var(--danger)" }}>⚠ {error}</p>}

                <button onClick={handleVerify} disabled={loading || otp.length !== 6}
                  style={{
                    marginTop: 16, width: "100%", borderRadius: 12,
                    background: (loading || otp.length !== 6) ? "var(--surface2)" : "linear-gradient(135deg, var(--brand), var(--brand-dark))",
                    color: (loading || otp.length !== 6) ? "var(--text2)" : "#fff", padding: "14px",
                    fontSize: 15, fontWeight: 700, fontFamily: "var(--font-display)",
                    cursor: (loading || otp.length !== 6) ? "not-allowed" : "pointer",
                    border: "none", transition: "all 0.2s",
                  }}>
                  {loading ? "Verifying..." : (mode === "signup" ? "Create Account" : "Verify & Login")}
                </button>

                <div style={{ marginTop: 14, textAlign: "center", fontSize: 13, color: "var(--text2)" }}>
                  {timer > 0
                    ? <>Resend in <strong style={{ color: "var(--brand)" }}>{timer}s</strong></>
                    : <button onClick={handleResend} style={{ background: "none", border: "none", color: "var(--brand)", fontWeight: 600, cursor: "pointer", fontSize: 13, fontFamily: "var(--font-body)" }}>Resend OTP</button>}
                </div>

                <button onClick={() => { setStep("details"); setOtp(""); setError(""); setTimer(0); }}
                  style={{ marginTop: 8, width: "100%", background: "none", border: "none", color: "var(--text3)", fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}>
                  ← {mode === "signup" ? "Edit details" : "Change phone number"}
                </button>
              </>
            )}

            <p style={{ marginTop: 20, fontSize: 12, color: "var(--text3)", textAlign: "center", lineHeight: 1.5 }}>
              🔒 Your information is encrypted and never shared with third parties
            </p>
          </div>

          <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "var(--text3)" }}>
            By continuing, you agree to our{" "}
            <Link to="/terms" style={{ color: "var(--brand)", fontWeight: 500 }}>Terms</Link>
            {" "}and{" "}
            <Link to="/privacy-policy" style={{ color: "var(--brand)", fontWeight: 500 }}>Privacy Policy</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
